import { FAIcon, Progress, Skeleton } from '@/components'
import { format, PERMISSIONS, useFormWatch, usePermissions } from '@/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, differenceInHours, startOfDay, startOfHour } from 'date-fns'
import type { ResultOf, VariablesOf } from 'gql.tada'
import { useEffect, useEffectEvent } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { ABSENCE_TYPE_PERIOD_REPORT_QUERY } from '../queries'
import type { AbsenceForm } from './absence-dialog'

type AbsenceTypePeriodReport = NonNullable<
  ResultOf<typeof ABSENCE_TYPE_PERIOD_REPORT_QUERY>['absenceTypePeriodReport']
>

type AbsenceTypePeriodReportForValidateTimespan = Pick<
  AbsenceTypePeriodReport,
  'absencesRemaining' | 'hoursRemaining'
> & {
  absenceType: Pick<
    AbsenceTypePeriodReport['absenceType'],
    'name' | 'durationMode' | 'maxHoursContinuous'
  >
}

function useValidateTimespan() {
  const translations = useTranslate({
    direction: 'absences.absence-dialog.report.validation.direction',

    noMoreAbsences: [
      'absences.absence-dialog.report.validation.no-more-absences',
      { name: <strong /> },
    ],

    maxContinuousHours: [
      'absences.absence-dialog.report.validation.max-continuous-hours',
      { name: <strong />, total: <strong />, hours: <strong /> },
    ],
    maxContinuousDays: [
      'absences.absence-dialog.report.validation.max-continuous-days',
      { name: <strong />, total: <strong />, days: <strong /> },
    ],

    notEnoughHours: [
      'absences.absence-dialog.report.validation.not-enough-hours',
      { name: <strong />, hours: <strong />, rem: <strong /> },
    ],
    notEnoughDays: [
      'absences.absence-dialog.report.validation.not-enough-days',
      { name: <strong />, days: <strong />, rem: <strong /> },
    ],
    noMoreHours: [
      'absences.absence-dialog.report.validation.no-more-hours',
      { name: <strong /> },
    ],
    noMoreDays: [
      'absences.absence-dialog.report.validation.no-more-days',
      { name: <strong /> },
    ],
  })

  return (
    report: AbsenceTypePeriodReportForValidateTimespan,
    start: Date,
    end: Date
  ): string | null => {
    if (start > end) return translations.direction

    const isDaily = report.absenceType.durationMode === 'DAILY'
    const absenceName = report.absenceType.name

    if (report.absencesRemaining !== null && report.absencesRemaining <= 0)
      return translations.noMoreAbsences({
        name: <strong>{absenceName}</strong>,
      })

    if (isDaily) {
      start = startOfDay(start)
      end = startOfDay(addDays(end, 1))
    } else {
      start = startOfHour(start)
      end = startOfHour(end)
    }

    // Manual math.max because of React Compiler
    let diffHours = differenceInHours(end, start)
    diffHours = diffHours < 0 ? 0 : diffHours

    // If max continuous is set, check that first
    if (
      !!report.absenceType.maxHoursContinuous &&
      diffHours > report.absenceType.maxHoursContinuous
    ) {
      if (isDaily)
        return translations.maxContinuousDays({
          name: <strong key="name">{absenceName}</strong>,
          days: <strong key="days">{Math.ceil(diffHours / 24)}</strong>,
          total: (
            <strong key="total">
              {Math.ceil(report.absenceType.maxHoursContinuous / 24)}
            </strong>
          ),
        })
      else
        return translations.maxContinuousHours({
          name: <strong key="name">{absenceName}</strong>,
          hours: <strong key="hours">{diffHours}</strong>,
          total: (
            <strong key="total">{report.absenceType.maxHoursContinuous}</strong>
          ),
        })
    }

    // Then check remaining hours/absences
    if (report.hoursRemaining !== null) {
      if (report.hoursRemaining <= 0) {
        if (isDaily)
          return translations.noMoreDays({
            name: <strong key="name">{absenceName}</strong>,
          })
        else
          return translations.noMoreHours({
            name: <strong key="name">{absenceName}</strong>,
          })
      }

      if (diffHours > report.hoursRemaining) {
        if (isDaily) {
          const daysRequested = Math.ceil(diffHours / 24)
          const daysAllowed = Math.floor(report.hoursRemaining / 24)

          return translations.notEnoughDays({
            name: <strong key="name">{absenceName}</strong>,
            days: <strong key="days">{daysRequested}</strong>,
            rem: <strong key="rem">{daysAllowed}</strong>,
          })
        } else {
          return translations.notEnoughHours({
            name: <strong key="name">{absenceName}</strong>,
            hours: <strong key="hours">{diffHours}</strong>,
            rem: <strong key="rem">{report.hoursRemaining}</strong>,
          })
        }
      }
    }

    return null
  }
}

type AbsenceTypePeriodReportForProgressInfo =
  AbsenceTypePeriodReportForValidateTimespan &
    Pick<
      AbsenceTypePeriodReport,
      'absencesMax' | 'absencesTotal' | 'hoursMax' | 'hoursUsed'
    >

interface ProgressInfo {
  value: number | null
  info: string
  warning: string | null
}

export function usePeriodProgressInfo() {
  const translations = useTranslate({
    used: {
      absences: [
        'absences.absence-dialog.report.used.absences',
        { used: <strong />, total: <strong /> },
      ],
      hours: [
        'absences.absence-dialog.report.used.hours',
        { used: <strong />, total: <strong /> },
      ],
      days: [
        'absences.absence-dialog.report.used.days',
        { used: <strong />, total: <strong /> },
      ],
    },
  })

  const validateTimespan = useValidateTimespan()

  function create(
    report: AbsenceTypePeriodReportForProgressInfo,
    datetimeStart?: Date,
    datetimeEnd?: Date
  ): ProgressInfo {
    let value: number | null = null
    let info: string = ''

    if (report.absencesMax === null || report.absencesRemaining === null) {
      if (report.absenceType.durationMode === 'DAILY')
        info = translations.used.days({
          used: <strong key="used">{Math.ceil(report.hoursUsed / 24)}</strong>,
          total: <strong key="total">{Math.ceil(report.hoursMax / 24)}</strong>,
        })
      else
        info = translations.used.hours({
          used: <strong key="used">{report.hoursUsed}</strong>,
          total: <strong key="total">{report.hoursMax}</strong>,
        })

      if (!!report.hoursMax)
        value = Math.min((report.hoursUsed / report.hoursMax) * 100, 100)
    } else {
      info = translations.used.absences({
        used: <strong key="used">{report.absencesTotal}</strong>,
        total: <strong key="total">{report.absencesMax}</strong>,
      })

      if (!!report.absencesMax)
        value = Math.min((report.absencesTotal / report.absencesMax) * 100, 100)
    }

    const warning =
      !!datetimeStart && !!datetimeEnd
        ? validateTimespan(report, datetimeStart, datetimeEnd)
        : null

    return {
      value,
      info,
      warning,
    }
  }

  return create
}

interface Props {
  form: UseFormReturn<AbsenceForm>

  onValidate(valid: boolean): void
}

export function AbsencePeriodReport({ form, onValidate }: Props) {
  const translations = useTranslate({
    interval: 'absences.absence-dialog.report.interval',
    usedLabel: 'absences.absence-dialog.report.used.label',

    noTimeOff: 'absences.absence-dialog.report.used.no-time-off',
    timeOffLabel: 'absences.absence-dialog.report.used.time-off-label',
    timeOffInvalid: 'absences.absence-dialog.report.used.time-off-invalid',
  })

  const { hasPermissions } = usePermissions()
  const canViewReport = hasPermissions(
    PERMISSIONS.timesheets.view.absenceperiodreport
  )

  const createProgressInfo = usePeriodProgressInfo()

  const datetimeStart = useFormWatch(form, 'datetimeStart')
  const datetimeEnd = useFormWatch(form, 'datetimeEnd')

  const queryVariables: VariablesOf<typeof ABSENCE_TYPE_PERIOD_REPORT_QUERY> = {
    userId: useFormWatch(form, 'user'),
    absenceTypeId: useFormWatch(form, 'absenceType'),
    datetimeStart: datetimeStart.toISOString(),
    datetimeEnd: datetimeEnd.toISOString(),
  }

  const enabled =
    canViewReport && !!queryVariables.userId && !!queryVariables.absenceTypeId

  const {
    data: reportData,
    loading: reportLoading,
    error: reportError,
  } = useQuery(ABSENCE_TYPE_PERIOD_REPORT_QUERY, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: queryVariables,
  })

  const report = reportData?.absenceTypePeriodReport ?? null

  const progress =
    !report || report.absenceType.preset === 'FLEXTIME'
      ? null
      : createProgressInfo(report, datetimeStart, datetimeEnd)

  const fireValidate = useEffectEvent(onValidate)

  const flextimeValid = (() => {
    if (report?.absenceType.preset !== 'FLEXTIME') return true

    const start = startOfHour(datetimeStart)
    const end = startOfHour(datetimeEnd)

    // Manual math.max because of React Compiler
    let diffHours = differenceInHours(end, start)
    diffHours = diffHours < 0 ? 0 : diffHours

    return report.hoursMax >= diffHours
  })()

  const isValid = (() => {
    if (!enabled || !!reportError) return true
    if (reportLoading) return false
    if (!flextimeValid) return false
    if (!progress) return true

    return !progress.warning
  })()

  useEffect(() => {
    fireValidate(isValid)
  }, [isValid])

  if (!enabled || !!reportError) return null

  return (
    <div className="flex p-4 rounded-xl border border-gray-200 text-sm">
      {(reportLoading && !reportData) || !report ? (
        <div className="w-full space-y-2">
          <Skeleton className="h-6 w-full rounded-md" />
          <Skeleton className="h-6 w-full rounded-md" />
        </div>
      ) : report.absenceType.preset !== 'FLEXTIME' ? (
        <div className="w-full space-y-2">
          <div>
            <span className="font-semibold">{translations.interval}</span>

            <span className="ml-1">
              {format(new Date(report.periodStart), 'PP')} -{' '}
              {format(new Date(report.periodEnd), 'PP')}
            </span>
          </div>

          {!!progress && (
            <div className="space-y-2">
              <div>
                <span className="font-semibold">{translations.usedLabel}</span>

                <span className="ml-1">{progress.info}</span>
              </div>

              {progress.value !== null && <Progress value={progress.value} />}

              {progress.warning && (
                <div className="flex gap-2 text-red-600">
                  <div>
                    <FAIcon icon="exclamation-triangle" type="solid" />
                  </div>
                  <span>{progress.warning}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full">
          {!report.hoursMax ? (
            <span className="text-red-600">{translations.noTimeOff}</span>
          ) : (
            <div className="flex flex-col gap-2">
              <div>
                <span className="font-semibold">
                  {translations.timeOffLabel}
                </span>
                <span className="ml-1">{report.hoursMax.toFixed(2)}</span>
              </div>

              {!flextimeValid && (
                <div className="text-red-600">
                  {translations.timeOffInvalid}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
